import React, { useState, useEffect } from 'react';
import { loginAsAdmin } from '../utils/LoginHelper';

const AuthDebug = () => {
  const [status, setStatus] = useState('Checking authentication...');
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    // Check current auth state
    const currentToken = localStorage.getItem('token');
    const currentUser = localStorage.getItem('user');
    
    setToken(currentToken);
    setUser(currentUser ? JSON.parse(currentUser) : null);
    
    if (currentToken) {
      setStatus('Token found in localStorage');
    } else {
      setStatus('No token found in localStorage');
    }
  }, []);

  const handleLogin = async () => {
    setStatus('Attempting to login...');
    try {
      // Make a direct API call to the login endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: 'admin@example.com',
          password: 'password'
        })
      });
      
      const data = await response.json();
      console.log('Login response:', data);
      
      if (data.success && data.data && data.data.token) {
        // Standard response format with nested data
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify({
          ...data.data.user,
          role: 'admin'
        }));
        
        setToken(data.data.token);
        setUser(data.data.user);
        setStatus('Login successful');
        return true;
      } else if (data.token) {
        // Alternative response format with token at root level
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify({
          ...data.user,
          role: 'admin'
        }));
        
        setToken(data.token);
        setUser(data.user);
        setStatus('Login successful');
        return true;
      } else {
        setStatus('Login failed: No token received');
        console.error('No token received from login');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setStatus(`Login error: ${error.message}`);
      return false;
    }
  };

  const handleTestAuth = async () => {
    setStatus('Testing authentication...');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/admin/vehicles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      console.log('Auth test response:', response);
      setTestResult({
        success: true,
        data: await response.json()
      });
      setStatus('Authentication test successful');
    } catch (error) {
      console.error('Auth test error:', error);
      setTestResult({
        success: false,
        error: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : error.message
      });
      setStatus(`Authentication test failed: ${error.response ? error.response.status : error.message}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setStatus('Logged out');
    setTestResult(null);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto my-8">
      <h2 className="text-2xl font-bold mb-4">Authentication Debug</h2>
      
      <div className="mb-6 p-4 bg-gray-100 rounded">
        <h3 className="text-lg font-semibold mb-2">Status: <span className="text-blue-600">{status}</span></h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium mb-1">API URL:</h4>
            <pre className="bg-gray-200 p-2 rounded text-sm overflow-auto">
              {process.env.REACT_APP_API_URL}
            </pre>
          </div>
          
          <div>
            <h4 className="font-medium mb-1">Token:</h4>
            <pre className="bg-gray-200 p-2 rounded text-sm overflow-auto max-h-20">
              {token ? token : 'No token'}
            </pre>
          </div>
        </div>
        
        {user && (
          <div className="mt-4">
            <h4 className="font-medium mb-1">User:</h4>
            <pre className="bg-gray-200 p-2 rounded text-sm overflow-auto max-h-40">
              {JSON.stringify(user, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      <div className="flex space-x-4 mb-6">
        <button
          onClick={handleLogin}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Login as Admin
        </button>
        
        <button
          onClick={handleTestAuth}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          disabled={!token}
        >
          Test Authentication
        </button>
        
        <button
          onClick={handleLogout}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          disabled={!token}
        >
          Logout
        </button>
      </div>
      
      {testResult && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Test Result:</h3>
          <div className={`p-4 rounded ${testResult.success ? 'bg-green-100' : 'bg-red-100'}`}>
            <pre className="text-sm overflow-auto max-h-60">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthDebug;
